import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi, locationsApi, employeesApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield,
  Users,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Key,
  Building2,
  Search,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
  UserCheck,
  RotateCcw,
} from 'lucide-react';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [formerUsers, setFormerUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('admins');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPinResetModal, setShowPinResetModal] = useState(null);
  const [newPin, setNewPin] = useState('1234');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Promote existing employee flow
  const [showCreationTypeModal, setShowCreationTypeModal] = useState(false);
  const [creationTypeRole, setCreationTypeRole] = useState('');
  const [showPromoteFlow, setShowPromoteFlow] = useState(false);
  const [promoteLocation, setPromoteLocation] = useState('');
  const [promoteEmployees, setPromoteEmployees] = useState([]);
  const [promoteSelectedEmployee, setPromoteSelectedEmployee] = useState(null);
  const [promoteTempPassword, setPromoteTempPassword] = useState('');
  const [promoteManagedLocations, setPromoteManagedLocations] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    roles: ['admin'],  // Support multiple roles
    locations: [],
    managed_location: '',
  });

  const [newPassword, setNewPassword] = useState('');

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, locationsData, formerData] = await Promise.all([
          usersApi.getAll(),
          locationsApi.getAll(),
          usersApi.getFormer().catch(() => []),
        ]);
        setUsers(usersData);
        setLocations(locationsData);
        setFormerUsers(formerData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleRestoreUser = async (userId) => {
    try {
      await usersApi.restore(userId);
      const [usersData, formerData] = await Promise.all([
        usersApi.getAll(),
        usersApi.getFormer().catch(() => []),
      ]);
      setUsers(usersData);
      setFormerUsers(formerData);
    } catch (error) {
      alert('Failed to restore user: ' + error.message);
    }
  };

  // Only admin and accounting_admin can access this page
  if (currentUser?.role !== 'admin' && currentUser?.role !== 'accounting_admin') {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">
            You do not have permission to manage users.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const admins = users.filter((u) => u.role === 'admin' || u.role === 'accounting_admin');
  const managers = users.filter((u) => u.role === 'manager');
  const assistantManagers = users.filter((u) => u.role === 'assistant_manager');

  const filteredUsers = activeTab === 'admins' ? admins : activeTab === 'managers' ? managers : assistantManagers;
  const searchedUsers = filteredUsers.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddUser = () => {
    let defaultRole = 'admin';
    let defaultRoles = ['admin'];
    if (activeTab === 'managers') {
      defaultRole = 'manager';
      defaultRoles = ['manager'];
    }
    if (activeTab === 'assistants') {
      defaultRole = 'assistant_manager';
      defaultRoles = ['assistant_manager'];
    }
    
    // For manager/assistant tabs, show creation type choice first
    if (activeTab === 'managers' || activeTab === 'assistants') {
      setCreationTypeRole(defaultRole);
      setShowCreationTypeModal(true);
      return;
    }

    setNewUser({
      name: '',
      email: '',
      password: '',
      role: defaultRole,
      roles: defaultRoles,
      locations: [],
      managed_locations: [],
      managed_location: '',
    });
    setEditingUser(null);
    setShowAddUser(true);
  };

  const handleNewEmployeeChoice = () => {
    setShowCreationTypeModal(false);
    setNewUser({
      name: '',
      email: '',
      password: '',
      role: creationTypeRole,
      roles: [creationTypeRole],
      locations: [],
      managed_locations: [],
      managed_location: '',
    });
    setEditingUser(null);
    setShowAddUser(true);
  };

  const handleExistingEmployeeChoice = () => {
    setShowCreationTypeModal(false);
    setPromoteLocation('');
    setPromoteEmployees([]);
    setPromoteSelectedEmployee(null);
    setPromoteTempPassword('');
    setPromoteManagedLocations([]);
    setShowPromoteFlow(true);
  };

  const handlePromoteLocationChange = async (locationId) => {
    setPromoteLocation(locationId);
    setPromoteSelectedEmployee(null);
    setLoadingEmployees(true);
    try {
      const emps = await employeesApi.getAll(locationId);
      // Only show active employees without an existing linked user
      const eligible = emps.filter(e => e.status === 'active' && !e.linked_user_id && !e.is_manager_employee);
      setPromoteEmployees(eligible);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setPromoteEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handlePromoteSubmit = async () => {
    if (!promoteSelectedEmployee || !promoteTempPassword) return;
    setSaving(true);
    try {
      const locs = promoteManagedLocations.length > 0 ? promoteManagedLocations : [promoteLocation];
      await employeesApi.promote(promoteSelectedEmployee.id, {
        role: creationTypeRole,
        password: promoteTempPassword,
        email: promoteSelectedEmployee.email,
        managed_locations: locs,
      });
      alert(`${promoteSelectedEmployee.name} promoted to ${creationTypeRole === 'manager' ? 'Manager' : 'Assistant Manager'}. They can now log in and change their password.`);
      setShowPromoteFlow(false);
      // Refresh users list
      const usersData = await usersApi.getAll();
      setUsers(usersData);
    } catch (error) {
      alert('Failed to promote employee: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (u) => {
    // Get roles array, fallback to single role if roles array is empty
    const userRoles = u.roles && u.roles.length > 0 ? u.roles : [u.role];
    
    setNewUser({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role,
      roles: userRoles,
      locations: u.locations || [],
      managed_locations: u.managed_locations || (u.managed_location ? [u.managed_location] : []),
      managed_location: u.managed_location || '',
    });
    setEditingUser(u);
    setShowAddUser(true);
  };

  const toggleRole = (role) => {
    const current = newUser.roles || [];
    if (current.includes(role)) {
      // Don't allow removing the last role
      if (current.length > 1) {
        const updated = current.filter(r => r !== role);
        setNewUser({ ...newUser, roles: updated, role: updated[0] });
      }
    } else {
      const updated = [...current, role];
      setNewUser({ ...newUser, roles: updated, role: updated[0] });
    }
  };

  const toggleLocation = (locId) => {
    const current = newUser.managed_locations || [];
    if (current.includes(locId)) {
      setNewUser({ ...newUser, managed_locations: current.filter(id => id !== locId) });
    } else {
      setNewUser({ ...newUser, managed_locations: [...current, locId] });
    }
  };

  // Helper to check if user has manager-like role
  const hasManagerRole = (roles) => {
    return roles && (roles.includes('manager') || roles.includes('assistant_manager'));
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      const userRoles = newUser.roles || [newUser.role];
      const primaryRole = userRoles[0];
      const isAdmin = userRoles.includes('admin') || userRoles.includes('accounting_admin');
      const isManager = hasManagerRole(userRoles);
      
      if (editingUser) {
        // Update user
        const managedLocs = newUser.managed_locations || [];
        const updateData = {
          name: newUser.name,
          email: newUser.email,
          role: primaryRole,
          roles: userRoles,
          locations: isAdmin ? locations.map((l) => l.id) : managedLocs,
          managed_locations: isManager ? managedLocs : [],
          managed_location: managedLocs[0] || null,
        };
        if (newUser.password) {
          updateData.password = newUser.password;
        }
        const updated = await usersApi.update(editingUser.id, updateData);
        setUsers(users.map((u) => (u.id === editingUser.id ? updated : u)));
      } else {
        // Create user
        const managedLocs = newUser.managed_locations || [];
        const userData = {
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          role: primaryRole,
          roles: userRoles,
          locations: isAdmin ? locations.map((l) => l.id) : managedLocs,
          managed_locations: isManager ? managedLocs : [],
          managed_location: managedLocs[0] || null,
        };
        const created = await usersApi.create(userData);
        setUsers([...users, created]);
      }
      setShowAddUser(false);
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !showPasswordModal) return;
    setSaving(true);
    try {
      await usersApi.update(showPasswordModal.id, { password: newPassword });
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setSaving(true);
    try {
      await usersApi.delete(userId);
      const [usersData, formerData] = await Promise.all([
        usersApi.getAll(),
        usersApi.getFormer().catch(() => []),
      ]);
      setUsers(usersData);
      setFormerUsers(formerData);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.message || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handlePinReset = async () => {
    if (!showPinResetModal) return;
    setSaving(true);
    try {
      await usersApi.resetPin(showPinResetModal.id, newPin);
      alert(`PIN reset to ${newPin} for ${showPinResetModal.name}`);
      setShowPinResetModal(null);
      setNewPin('1234');
    } catch (error) {
      console.error('Error resetting PIN:', error);
      alert(error.message || 'Failed to reset PIN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">Manage admin, manager, and assistant manager accounts</p>
        </div>
        <Button onClick={handleAddUser} className="bg-purple-600 hover:bg-purple-700" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add {activeTab === 'admins' ? 'Admin' : activeTab === 'managers' ? 'Manager' : 'Assistant Manager'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Admins</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{admins.length}</p>
              </div>
              <div className="bg-red-500 p-3 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Managers</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{managers.length}</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Asst. Managers</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{assistantManagers.length}</p>
              </div>
              <div className="bg-violet-500 p-3 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Locations</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{locations.length}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-xl">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Search */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="admins" className="gap-2" data-testid="admins-tab">
                  <Shield className="w-4 h-4" />
                  Admins ({admins.length})
                </TabsTrigger>
                <TabsTrigger value="managers" className="gap-2" data-testid="managers-tab">
                  <Users className="w-4 h-4" />
                  Managers ({managers.length})
                </TabsTrigger>
                <TabsTrigger value="assistants" className="gap-2" data-testid="assistants-tab">
                  <Users className="w-4 h-4" />
                  Asst. Managers ({assistantManagers.length})
                </TabsTrigger>
                <TabsTrigger value="former" className="gap-2" data-testid="former-users-tab">
                  <Trash2 className="w-4 h-4" />
                  Former ({formerUsers.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      {activeTab !== 'former' ? (
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">User</TableHead>
                <TableHead className="font-medium">Role</TableHead>
                {activeTab === 'managers' && (
                  <TableHead className="font-medium">Assigned Location</TableHead>
                )}
                <TableHead className="font-medium">Access</TableHead>
                <TableHead className="font-medium w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchedUsers.map((u) => {
                const managedLocs = u.managed_locations || (u.managed_location ? [u.managed_location] : []);
                const assignedLocations = locations.filter((l) => managedLocs.includes(l.id));
                return (
                  <TableRow key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={u.avatar} alt={u.name} />
                          <AvatarFallback className="bg-gray-200">
                            {u.name?.split(' ').map((n) => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-gray-900">{u.name}</p>
                          <p className="text-sm text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {/* Show all roles if user has multiple, otherwise show primary role */}
                        {(u.roles && u.roles.length > 0 ? u.roles : [u.role]).map((role) => (
                          <Badge
                            key={role}
                            variant="secondary"
                            className={`${
                              role === 'admin' || role === 'accounting_admin'
                                ? 'bg-red-100 text-red-700'
                                : role === 'assistant_manager'
                                ? 'bg-violet-100 text-violet-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}
                          >
                            {role === 'accounting_admin' ? 'Acct Admin' : role === 'assistant_manager' ? 'Asst. Mgr' : role}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    {(activeTab === 'managers' || activeTab === 'assistants') && (
                      <TableCell>
                        {assignedLocations.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {assignedLocations.map((loc) => (
                              <Badge key={loc.id} variant="outline" className="text-xs">
                                <Building2 className="w-3 h-3 mr-1" />
                                {loc.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-amber-600">Not assigned</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className="text-sm text-gray-600">
                        {u.role === 'admin' || u.role === 'accounting_admin'
                          ? 'Full access'
                          : u.role === 'assistant_manager'
                          ? 'Schedule & Messages only'
                          : assignedLocations.length > 0
                          ? `${assignedLocations.length} location(s)`
                          : 'No access'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-menu-${u.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(u)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setShowPasswordModal(u)}>
                            <Key className="w-4 h-4 mr-2" />
                            Change Password
                          </DropdownMenuItem>
                          {/* Show PIN reset for managers */}
                          {(u.role === 'manager' || (u.roles && u.roles.includes('manager'))) && (
                            <DropdownMenuItem onClick={() => setShowPinResetModal(u)}>
                              <Key className="w-4 h-4 mr-2" />
                              Reset Payroll PIN
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setShowDeleteConfirm(u)}
                            className="text-red-600"
                            disabled={u.id === currentUser?.id}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {searchedUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No users found</p>
            </div>
          )}
        </CardContent>
      </Card>
      ) : (
      <Card className="border-0 shadow-md" data-testid="former-users-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">User</TableHead>
                <TableHead className="font-medium">Role</TableHead>
                <TableHead className="font-medium">Terminated</TableHead>
                <TableHead className="font-medium">By</TableHead>
                <TableHead className="font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formerUsers
                .filter(u =>
                  !searchQuery ||
                  u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((u) => (
                <TableRow key={u.id} className="hover:bg-gray-50" data-testid={`former-user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 opacity-60">
                        <AvatarImage src={u.avatar} alt={u.name} />
                        <AvatarFallback className="bg-gray-200">
                          {u.name?.split(' ').map((n) => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                      {u.role === 'accounting_admin' ? 'Acct Admin' : u.role === 'assistant_manager' ? 'Asst. Mgr' : u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {u.terminated_at ? new Date(u.terminated_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">{u.terminated_by_name || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-700 border-green-200 hover:bg-green-50"
                      onClick={() => handleRestoreUser(u.id)}
                      data-testid={`restore-user-${u.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {formerUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Trash2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No former users</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add/Edit User Modal */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : `Add New ${newUser.role === 'admin' || newUser.role === 'accounting_admin' ? 'Admin' : 'Manager'}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Smith"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@company.com"
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                  data-testid="user-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Multi-Role Selection */}
            <div className="space-y-2">
              <Label>Roles {editingUser ? '(select roles to update)' : ''}</Label>
              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-admin"
                    checked={(newUser.roles || []).includes('admin')}
                    onCheckedChange={() => toggleRole('admin')}
                    data-testid="role-admin-checkbox"
                  />
                  <label htmlFor="role-admin" className="text-sm font-medium cursor-pointer">
                    Admin <span className="text-gray-400">(Full Access)</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-accounting"
                    checked={(newUser.roles || []).includes('accounting_admin')}
                    onCheckedChange={() => toggleRole('accounting_admin')}
                    data-testid="role-accounting-checkbox"
                  />
                  <label htmlFor="role-accounting" className="text-sm font-medium cursor-pointer">
                    Accounting Admin <span className="text-gray-400">(Full Access + Salaries)</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-manager"
                    checked={(newUser.roles || []).includes('manager')}
                    onCheckedChange={() => toggleRole('manager')}
                    data-testid="role-manager-checkbox"
                  />
                  <label htmlFor="role-manager" className="text-sm font-medium cursor-pointer">
                    Manager <span className="text-gray-400">(Location Access)</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-assistant"
                    checked={(newUser.roles || []).includes('assistant_manager')}
                    onCheckedChange={() => toggleRole('assistant_manager')}
                    data-testid="role-assistant-checkbox"
                  />
                  <label htmlFor="role-assistant" className="text-sm font-medium cursor-pointer">
                    Assistant Manager <span className="text-gray-400">(Limited Access)</span>
                  </label>
                </div>
              </div>
              {(newUser.roles || []).length > 0 && (
                <p className="text-xs text-gray-500">
                  {(newUser.roles || []).length} role(s) selected: {(newUser.roles || []).join(', ')}
                </p>
              )}
            </div>

            {/* Location Assignment - show if manager or assistant_manager is selected */}
            {hasManagerRole(newUser.roles) && (
              <div className="space-y-2">
                <Label>Assign to Locations</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {locations.length === 0 ? (
                    <p className="text-sm text-gray-500">No locations available. Create a location first.</p>
                  ) : (
                    locations.map((loc) => (
                      <div key={loc.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`loc-${loc.id}`}
                          checked={(newUser.managed_locations || []).includes(loc.id)}
                          onCheckedChange={() => toggleLocation(loc.id)}
                        />
                        <label
                          htmlFor={`loc-${loc.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {loc.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {(newUser.managed_locations || []).length > 0 && (
                  <p className="text-xs text-gray-500">
                    {(newUser.managed_locations || []).length} location(s) selected
                  </p>
                )}
              </div>
            )}

            {/* Role information notes */}
            {((newUser.roles || []).includes('admin') || (newUser.roles || []).includes('accounting_admin')) && (
              <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
                <p><strong>Note:</strong> Admin/Accounting Admin roles have full access to all locations and can manage other users.</p>
              </div>
            )}

            {(newUser.roles || []).includes('assistant_manager') && (
              <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
                <p><strong>Assistant Manager Access:</strong></p>
                <ul className="list-disc ml-4 mt-1">
                  <li>View and edit schedules</li>
                  <li>View time entries (read-only)</li>
                  <li>Send messages to team</li>
                </ul>
                <p className="mt-2 text-purple-600"><strong>No access to:</strong> Payroll, Audit logs, User management</p>
              </div>
            )}

            {(newUser.roles || []).length > 1 && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p><strong>Multiple Roles:</strong> This user will have combined permissions from all selected roles.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={
                saving ||
                !newUser.name ||
                !newUser.email ||
                (!editingUser && !newUser.password) ||
                (newUser.roles || []).length === 0 ||
                (hasManagerRole(newUser.roles) && (newUser.managed_locations || []).length === 0)
              }
              data-testid="save-user-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingUser ? 'Update' : 'Create'} User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={!!showPasswordModal} onOpenChange={() => setShowPasswordModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Set a new password for <strong>{showPasswordModal?.name}</strong>
            </p>

            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700 mt-4">
              <p>This action will be logged in the audit trail.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={!newPassword || saving}
              data-testid="change-password-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <span className="font-medium text-gray-900">{showDeleteConfirm?.name}</span>?
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteUser(showDeleteConfirm?.id)}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Reset Modal */}
      <Dialog open={!!showPinResetModal} onOpenChange={() => setShowPinResetModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Reset Payroll PIN
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 py-2">
            Set a new payroll access PIN for <strong>{showPinResetModal?.name}</strong>
          </p>
          <div className="space-y-2">
            <Label>New PIN (4 digits)</Label>
            <Input
              type="text"
              maxLength={4}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              className="font-mono text-center text-lg tracking-widest"
              data-testid="new-pin-input"
            />
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mt-2">
            <p>The manager will use this PIN to access payroll data.</p>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPinResetModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handlePinReset}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={newPin.length !== 4 || saving}
              data-testid="confirm-pin-reset-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Creation Type Modal - New or Existing Employee */}
      <Dialog open={showCreationTypeModal} onOpenChange={setShowCreationTypeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add {creationTypeRole === 'manager' ? 'Manager' : 'Assistant Manager'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">How would you like to create this {creationTypeRole === 'manager' ? 'Manager' : 'Assistant Manager'}?</p>
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={handleNewEmployeeChoice}
              className="flex flex-col items-center gap-3 p-6 border-2 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
              data-testid="new-employee-choice"
            >
              <UserPlus className="w-10 h-10 text-purple-600" />
              <span className="font-semibold text-gray-900">New Employee</span>
              <span className="text-xs text-gray-500 text-center">Create a brand new profile from scratch</span>
            </button>
            <button
              onClick={handleExistingEmployeeChoice}
              className="flex flex-col items-center gap-3 p-6 border-2 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all cursor-pointer"
              data-testid="existing-employee-choice"
            >
              <UserCheck className="w-10 h-10 text-green-600" />
              <span className="font-semibold text-gray-900">Existing Employee</span>
              <span className="text-xs text-gray-500 text-center">Promote an existing employee (keeps history)</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote Existing Employee Flow */}
      <Dialog open={showPromoteFlow} onOpenChange={setShowPromoteFlow}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-600" />
              Promote Existing Employee to {creationTypeRole === 'manager' ? 'Manager' : 'Assistant Manager'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Step 1: Choose Location */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Step 1: Choose Location</Label>
              <Select value={promoteLocation} onValueChange={handlePromoteLocationChange}>
                <SelectTrigger data-testid="promote-location-select">
                  <SelectValue placeholder="Select a location..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Choose Employee */}
            {promoteLocation && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Step 2: Choose Employee</Label>
                {loadingEmployees ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading employees...
                  </div>
                ) : promoteEmployees.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">No eligible employees at this location. Employees who already have a user account are excluded.</p>
                ) : (
                  <Select
                    value={promoteSelectedEmployee?.id || ''}
                    onValueChange={(val) => {
                      const emp = promoteEmployees.find(e => e.id === val);
                      setPromoteSelectedEmployee(emp);
                      setPromoteManagedLocations([promoteLocation]);
                    }}
                  >
                    <SelectTrigger data-testid="promote-employee-select">
                      <SelectValue placeholder="Select an employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {promoteEmployees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.employee_number}) — {emp.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Step 3: Assign Role (auto) + Temp Password */}
            {promoteSelectedEmployee && (
              <>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    <strong>{promoteSelectedEmployee.name}</strong> will be assigned the <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 mx-1">{creationTypeRole === 'manager' ? 'Manager' : 'Assistant Manager'}</span> role.
                    Their employment history will be preserved.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Step 3: Set Temporary Password</Label>
                  <p className="text-xs text-gray-500">The employee can change this after their first login via their profile.</p>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={promoteTempPassword}
                      onChange={(e) => setPromoteTempPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      data-testid="promote-temp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Managed Locations</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                    {locations.map((loc) => (
                      <div key={loc.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`promo-loc-${loc.id}`}
                          checked={promoteManagedLocations.includes(loc.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setPromoteManagedLocations(prev => [...prev, loc.id]);
                            } else {
                              setPromoteManagedLocations(prev => prev.filter(l => l !== loc.id));
                            }
                          }}
                        />
                        <label htmlFor={`promo-loc-${loc.id}`} className="text-sm cursor-pointer">{loc.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteFlow(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handlePromoteSubmit}
              disabled={saving || !promoteSelectedEmployee || !promoteTempPassword || promoteTempPassword.length < 6 || promoteManagedLocations.length === 0}
              data-testid="promote-submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Promote to {creationTypeRole === 'manager' ? 'Manager' : 'Asst. Manager'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
